import { ActionModal, TextInput } from "@/components";
import HierarchicalCheckbox from "@/components/checkbox/hierarchical";
import { PAGE_OPTIONS, filterOptionsByAvailable, normalizeForStorage, expandForDisplay, collectAllKeys } from "@/roles/protection/pageOptions";
import { useGetTenantAllowedPagesQuery, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { Typography } from "@mui/material";
import { userRightOptions } from "@/constants";
import { Box, Grid } from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { StyledSelectBox, StyledSelectBoxContainer } from "./styles";
import { ValidationPatterns } from "@/utils/form-validation";
import InputErrorMessage from "@/components/input-error-message";
import { useSnackbar } from "@/hooks";
import { tokens } from "@/tokens";

interface DataModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  buttonAction: (data: IUser) => void;
  buttonText: string;
  isEdit: IUser | null;
  buttonLoader: boolean;
}

const AddEditUserModal: React.FC<DataModalProps> = ({
  setShow,
  show,
  title,
  buttonText,
  buttonAction,
  isEdit,
  buttonLoader,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IUser>({
    defaultValues: {
      userRight: isEdit ? isEdit.accessLevel : userRightOptions[0].value,
      firstName: isEdit ? isEdit?.firstName : "",
      lastName: isEdit ? isEdit?.lastName : "",
      email: isEdit ? isEdit?.email : "",
    },
  });

  const [allowedPages, setAllowedPages] = React.useState<string[]>(
    expandForDisplay(isEdit?.allowedPages || [], PAGE_OPTIONS)
  );
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery();
  const [setUserAllowedPages] = useSetUserAllowedPagesMutation();
  const [savingPages, setSavingPages] = React.useState(false);

  const availablePageKeys = React.useMemo(
    () => tenantPagesResp?.pages || [],
    [tenantPagesResp]
  );
  const filteredOptions = React.useMemo(
    () => filterOptionsByAvailable(PAGE_OPTIONS, availablePageKeys),
    [availablePageKeys]
  );
  const isSelectionDisabled = filteredOptions.length === 0;

  const onSubmit = async (data: IUser) => {
    const validKeys = collectAllKeys(filteredOptions);
    const finalPages = normalizeForStorage(allowedPages.filter((k) => validKeys.has(k)), PAGE_OPTIONS);
    // For edit flow, save allowed pages first. If it fails, inform the user and keep modal open.
    if (isEdit?._id) {
      try {
        setSavingPages(true);
        await setUserAllowedPages({ userId: isEdit._id, pages: finalPages }).unwrap();
      } catch (err: any) {
        const description =
          err?.data?.message || err?.error || err?.message || "Please try again or contact support.";
        showSnackbar("Failed to update page access", description, "error", 6000);
        setSavingPages(false);
        return;
      }
      setSavingPages(false);
    }
    // Proceed with the primary create/update action
    await buttonAction({ ...data, allowedPages: finalPages });
  };

  const formFields: Array<{
    name: keyof IUser;
    label: string;
    type: string;
    rules: object;
    placeholder: string;
    disabled?: boolean;
  }> = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      rules: { required: "First name is required" },
      placeholder: "eg John",
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      rules: { required: "Last name is required" },
      placeholder: "eg Snow",
    },
    {
      name: "email",
      label: "Email Address*",
      type: "email",
      placeholder: "hi@example.com",
      disabled: !!isEdit,
      rules: {
        required: "Email is required",
        pattern: {
          value: ValidationPatterns.email,
          message: "Invalid email address",
        },
      },
    },
  ];

  return (
    <ActionModal
      setShow={setShow}
      show={show}
      buttonLoader={buttonLoader}
      isConfirmButtonLoading={buttonLoader || savingPages}
      title={title}
      buttonText={buttonText}
      buttonAction={handleSubmit(onSubmit)}
      modalStyles={{
        "& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded": {
          width: "500px",
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <StyledSelectBoxContainer>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="userRight"
                control={control}
                rules={{ required: "Role is required" }}
                render={({ field }) => {
                  return (
                    <StyledSelectBox
                      disabled={false}
                      value={field.value as string}
                      options={userRightOptions}
                      label="Role"
                      handleChangeValue={field.onChange}
                    />
                  );
                }}
              />
              <InputErrorMessage message={errors.userRight?.message} />
            </Grid>

            {formFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={field.name === "email" ? 12 : 6}
                key={field.name}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      disabled={field.disabled}
                      placeholder={field.placeholder}
                      label={field.label}
                      type={field.type}
                      value={value as string}
                      handleChangeValue={onChange}
                      error={!!errors[field.name as keyof typeof errors]}
                    />
                  )}
                />
                <InputErrorMessage
                  message={errors[field.name as keyof typeof errors]?.message}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <HierarchicalCheckbox
              options={filteredOptions}
              selectedKeys={allowedPages}
              onChange={setAllowedPages}
            />
            {isSelectionDisabled && (
              <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base, mt: 0.5 }}>
                No tenant pages configured. Contact your administrator.
              </Typography>
            )}
          </Grid>
        </StyledSelectBoxContainer>
      </Box>
    </ActionModal>
  );
};

export default AddEditUserModal;
